import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  CloudOff,
  Download,
  FileUp,
  Loader2,
  Network,
  Radio,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import TransformationDiagram from '@/components/TransformationDiagram';
import AuraProjectChat from '@/components/AuraProjectChat';
import AuraExternalSystemsLegend from '@/components/AuraExternalSystemsLegend';
import AuraSourceTooltip from '@/components/AuraSourceTooltip';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  analyzeOffline,
  getAuraProject,
  statusLabel,
  waitForAuraOfflineRun,
} from '@/lib/auraApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import {
  CUSTOM_CODE_TILE_SOURCES,
  IDENTITY_SOURCE_MAP,
  functionalAreaSource,
  impactSource,
  integrationRowSource,
  kpiSource,
  resolveSectionSource,
} from '@/lib/auraSourceAttribution';
import {
  diagramNodeCount,
  ensureDiagramFromInventory,
  unwrapArtifactContent,
} from '@/lib/auraDiagramFallback';
import { getExternalSystemsFromInventory } from '@/lib/auraExternalSystems';

const WORKSTREAM_LABELS = {
  custom_code: 'Custom code',
  integration: 'Integration',
  data_migration: 'Data migration',
  finance: 'Finance',
  simplification: 'Simplification',
  addons: 'Add-ons',
  cvi: 'CVI / BP',
  testing_cutover: 'Testing & cutover',
  org_master_data: 'Org / master data',
  fiori_ux: 'Fiori / UX',
  operations: 'Operations',
  other: 'Other',
};

function formatWorkstreamLabel(workstream) {
  const key = String(workstream || '').trim().toLowerCase();
  return WORKSTREAM_LABELS[key] || key.replace(/_/g, ' ') || 'Other';
}

function normalizeInterviewQuestion(item, index) {
  if (typeof item === 'string') {
    const question = item.trim();
    if (!question) return null;
    return {
      id: `q_${index + 1}`,
      question,
      rc_evidence: '',
      workstream: 'other',
      sizing_input: '',
      expected_answer_type: 'unknown',
      estimation_impact: 'medium',
    };
  }
  if (!item || typeof item !== 'object') return null;
  const question = String(item.question || '').trim();
  if (!question) return null;
  return {
    id: String(item.id || `q_${index + 1}`),
    question,
    rc_evidence: String(item.rc_evidence || '').trim(),
    workstream: String(item.workstream || 'other').trim().toLowerCase(),
    sizing_input: String(item.sizing_input || '').trim(),
    expected_answer_type: String(item.expected_answer_type || 'unknown').trim().toLowerCase(),
    estimation_impact: String(item.estimation_impact || 'medium').trim().toLowerCase(),
  };
}

function impactBadgeClass(impact) {
  const value = String(impact || '').toLowerCase();
  if (value === 'high') return 'border-rose-500/40 bg-rose-500/10 text-foreground';
  if (value === 'low') return 'border-emerald-500/40 bg-emerald-500/10 text-foreground';
  return 'border-amber-500/40 bg-amber-500/10 text-foreground';
}

/** Sub-agent stages shown to the user (never show orchestrators). */
const AGENT_DEFS = [
  {
    id: 'landscape',
    name: 'Landscape Discovery Agent',
    description: 'Connects to SAP and prepares package for analysis.',
  },
  {
    id: 'analysis',
    name: 'Analysis Agent',
    description: 'Generates dashboard metrics, landscape diagram, and dataflow diagram.',
  },
];

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'in_progress' || s === 'discovery') {
    return 'border-sky-500/40 bg-sky-500/15 text-foreground';
  }
  if (s === 'draft') {
    return 'border-[var(--line)] bg-[var(--inset)] text-[var(--text-muted)]';
  }
  return 'border-emerald-500/40 bg-emerald-500/15 text-foreground';
}

function fmtIface(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const parts = [];
  if (obj.total != null) parts.push(`total ${obj.total}`);
  if (obj.unavailable != null) parts.push(`unavailable ${obj.unavailable}`);
  if (obj.blocked != null) parts.push(`blocked ${obj.blocked}`);
  if (obj.notes) parts.push(obj.notes);
  return parts.length ? parts.join(' · ') : null;
}

function numOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatMetricValue(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') {
    if (v >= 1000) return v.toLocaleString();
    return String(v);
  }
  return String(v);
}

/**
 * Build dashboard KPI tiles from inventory (RC-extractable metrics).
 */
function buildDashboardMetrics(inventory) {
  if (!inventory || typeof inventory !== 'object') {
    return {
      kpis: [],
      integration: [],
      identity: [],
      impacts: [],
      workshop: null,
      score: null,
      functionalAreas: [],
      customCode: null,
    };
  }
  const integ = inventory.integration || {};
  const addons = inventory.addons || {};
  const sim = inventory.simplification || {};
  const code = inventory.custom_code || {};
  const sizing = inventory.sizing || {};
  const scoreObj = inventory.customization_score || {};
  const fiori = inventory.fiori || {};
  const downtime = inventory.downtime || {};
  const ml = inventory.finance?.material_ledger || {};
  const blockers = Array.isArray(inventory.blockers) ? inventory.blockers : [];
  const nextSteps = Array.isArray(inventory.next_steps) ? inventory.next_steps : [];
  const openQ = Array.isArray(inventory.open_questions) ? inventory.open_questions : [];
  const finRisks = Array.isArray(inventory.finance?.risks) ? inventory.finance.risks : [];
  const cviOpen = Array.isArray(inventory.cvi?.open_items) ? inventory.cvi.open_items : [];
  const impacts = Array.isArray(inventory.mandatory_impacts)
    ? inventory.mandatory_impacts
    : [];
  const workshop = inventory.workshop && typeof inventory.workshop === 'object'
    ? inventory.workshop
    : null;
  const functionalAreas = Array.isArray(inventory.functional_areas)
    ? inventory.functional_areas.filter((a) => a?.name && a?.evidence)
    : [];

  const ifaceFamilies = [
    ['IDoc', integ.idoc],
    ['Web services', integ.web_services],
    ['RFC / BAPI', integ.rfc_bapi],
    ['BW extractors', integ.bw_extractors],
    ['OData', integ.odata],
    ['SLT', integ.slt],
    ['Flat file', integ.flat_file],
  ];

  let ifaceTotal = 0;
  let ifaceRisk = 0;
  let ifaceWithData = 0;
  const integration = [];
  ifaceFamilies.forEach(([label, obj]) => {
    if (!obj || typeof obj !== 'object') return;
    const total = numOrNull(obj.total);
    const unavail = numOrNull(obj.unavailable) || 0;
    const blocked = numOrNull(obj.blocked) || 0;
    if (total == null && !obj.notes) return;
    ifaceWithData += 1;
    if (total != null) ifaceTotal += total;
    ifaceRisk += unavail + blocked;
    integration.push({
      label,
      total: total != null ? total : '—',
      risk: unavail + blocked,
      hint: fmtIface(obj),
      source: integrationRowSource(label, obj),
    });
  });

  const rollupTotal = numOrNull(integ.total) ?? (ifaceWithData ? ifaceTotal : null);
  const rollupRisk = numOrNull(integ.risk_total) ?? (ifaceRisk || null);

  const kpis = [
    {
      id: 'score',
      label: 'Customization score',
      value: numOrNull(scoreObj.score),
      sub: scoreObj.band
        ? `${String(scoreObj.band).toUpperCase()} · relative RC signal`
        : 'Relative to standard S/4',
      tone:
        scoreObj.band === 'high'
          ? 'danger'
          : scoreObj.band === 'medium'
            ? 'warn'
            : scoreObj.band === 'low'
              ? 'ok'
              : 'info',
    },
    {
      id: 'impacts',
      label: 'Mandatory impacts',
      value: impacts.length > 0 ? impacts.length : null,
      sub: 'SI / ML / CVI / add-ons / finance',
      tone: impacts.some((i) => i?.severity === 'blocker') ? 'danger' : 'warn',
    },
    {
      id: 'addons',
      label: 'Add-ons',
      value: numOrNull(addons.total),
      sub:
        addons.incompatible != null
          ? `${addons.incompatible} incompatible`
          : addons.compatible != null
            ? `${addons.compatible} compatible`
            : 'Compatibility scope',
      tone: numOrNull(addons.incompatible) > 0 ? 'warn' : 'default',
    },
    {
      id: 'simplif',
      label: 'Simplification items',
      value:
        numOrNull(sim.total_items_mentioned)
        ?? numOrNull(sim.relevant_count),
      sub:
        sim.relevant_count != null || sim.to_check_count != null
          ? [
            sim.relevant_count != null ? `${sim.relevant_count} relevant` : null,
            sim.to_check_count != null ? `${sim.to_check_count} to check` : null,
          ]
            .filter(Boolean)
            .join(' · ')
          : Array.isArray(sim.high_effort_themes) && sim.high_effort_themes.length
            ? `${sim.high_effort_themes.length} high-effort themes`
            : 'Conversion impact',
      tone: 'default',
    },
    {
      id: 'custom_findings',
      label: 'ATC findings',
      value: numOrNull(code.total_findings),
      sub: code.analyzed_system
        ? `Analyzed on ${code.analyzed_system}`
        : 'Custom code ATC total',
      tone: (numOrNull(code.total_findings) || 0) > 1000 ? 'warn' : 'default',
    },
    {
      id: 'custom_redesign',
      label: 'Functional redesign',
      value:
        numOrNull(code.functional_redesign)
        ?? numOrNull(code.findings_in_scope_functional_manual),
      sub: 'In-scope functional changes (manual)',
      tone: 'warn',
    },
    {
      id: 'custom_tech',
      label: 'Technical adoption',
      value:
        numOrNull(code.technical_code_adoption)
        ?? (
          (numOrNull(code.findings_in_scope_technical_manual) || 0)
          + (numOrNull(code.findings_in_scope_technical_quick_fix) || 0)
          || null
        ),
      sub: (() => {
        const qf = numOrNull(code.findings_in_scope_technical_quick_fix)
          ?? numOrNull(code.quick_fix_supported);
        return qf != null ? `${qf.toLocaleString()} with quick fix support` : 'Technical code adaptation';
      })(),
      tone: 'default',
    },
    {
      id: 'custom',
      label: 'Custom code objects',
      value:
        numOrNull(code.objects_in_scope)
        ?? numOrNull(code.sycm_custom_objects),
      sub: code.sycm_modifications != null
        ? `SYCM modifications ${Number(code.sycm_modifications).toLocaleString()}`
        : code.adjustment_effort_notes
          ? String(code.adjustment_effort_notes).slice(0, 48)
          : 'Objects stated in RC (not full WRICEF list)',
      tone: (
        numOrNull(code.objects_in_scope)
        || numOrNull(code.sycm_custom_objects)
        || 0
      ) > 5000
        ? 'warn'
        : 'default',
    },
    {
      id: 'hana',
      label: 'HANA memory (GB)',
      value: numOrNull(sizing.hana_memory_gb),
      sub: sizing.simulation_notes
        ? String(sizing.simulation_notes).slice(0, 48)
        : 'Sizing simulation',
      tone: 'info',
    },
    {
      id: 'iface',
      label: 'Interfaces (total)',
      value: rollupTotal,
      sub: rollupRisk
        ? `${rollupRisk} unavailable / blocked`
        : ifaceWithData
          ? `${ifaceWithData} interface families`
          : 'Integration volume',
      tone: rollupRisk > 0 ? 'warn' : 'default',
    },
    {
      id: 'blockers',
      label: 'Blockers',
      value: blockers.length,
      sub: blockers.length ? 'Must resolve for conversion' : 'None listed in extract',
      tone: blockers.length ? 'danger' : 'ok',
    },
    {
      id: 'ml',
      label: 'Material Ledger',
      value: ml.status && ml.status !== 'unknown' ? ml.status.replace(/_/g, ' ') : null,
      sub:
        ml.mandatory_for_conversion === true
          ? 'Mandatory for conversion'
          : ml.notes
            ? String(ml.notes).slice(0, 48)
            : 'Finance gate',
      tone:
        ml.mandatory_for_conversion === true || /inconsist|required/i.test(ml.status || '')
          ? 'warn'
          : 'default',
    },
    {
      id: 'finance',
      label: 'Finance risks',
      value: finRisks.length > 0 ? finRisks.length : null,
      sub: 'GL / AA / ML data quality',
      tone: finRisks.length ? 'warn' : 'default',
    },
    {
      id: 'cvi',
      label: 'CVI open items',
      value: cviOpen.length > 0 ? cviOpen.length : null,
      sub:
        inventory.cvi?.mandatory_for_conversion === true
          ? 'Mandatory for conversion'
          : inventory.cvi?.status_notes
            ? String(inventory.cvi.status_notes).slice(0, 48)
            : 'Business Partner',
      tone: cviOpen.length || inventory.cvi?.mandatory_for_conversion ? 'warn' : 'default',
    },
    {
      id: 'fiori',
      label: 'Fiori apps',
      value: numOrNull(fiori.recommended_count),
      sub: fiori.availability_notes
        ? String(fiori.availability_notes).slice(0, 48)
        : 'Recommended for S/4',
      tone: 'info',
    },
    {
      id: 'downtime',
      label: 'Downtime (hrs)',
      value: numOrNull(downtime.hours),
      sub: downtime.estimate_notes
        ? String(downtime.estimate_notes).slice(0, 48)
        : downtime.calculator_present
          ? 'From downtime calculator'
          : 'Cutover estimate',
      tone: 'info',
    },
    {
      id: 'next',
      label: 'Next steps',
      value: nextSteps.length > 0 ? nextSteps.length : null,
      sub: 'Recommended follow-ups',
      tone: 'info',
    },
    {
      id: 'questions',
      label: 'Open questions',
      value: openQ.length > 0 ? openQ.length : null,
      sub: 'Workshop / discovery',
      tone: 'default',
    },
    {
      id: 'modules',
      label: 'Functional areas (evidenced)',
      value: functionalAreas.length > 0 ? functionalAreas.length : null,
      sub: 'Named in RC only — not a full module inventory',
      tone: 'info',
    },
    {
      id: 'interview',
      label: 'Interview guides',
      value: Array.isArray(workshop?.interview_questions)
        ? workshop.interview_questions.length || null
        : null,
      sub: 'Effort-sizing workshop questions',
      tone: 'info',
    },
  ]
    .filter((k) => k.value != null && k.value !== '')
    .map((k) => ({
      ...k,
      source: kpiSource(k.id, inventory),
      sourceTitle: k.id === 'score' ? 'How this score was calculated' : 'Source',
      tooltipClassName: k.id === 'score' ? 'max-w-sm' : '',
    }));

  const identity = [
    { label: 'Customer', value: inventory.customer_name },
    { label: 'SID', value: inventory.sid },
    { label: 'Source', value: inventory.source_product_version },
    { label: 'Target S/4', value: inventory.target_s4_version },
    { label: 'Database', value: inventory.current_database },
    { label: 'OS / kernel', value: [inventory.operating_system, inventory.kernel].filter(Boolean).join(' · ') || null },
    { label: 'Analysis date', value: inventory.analysis_date },
  ]
    .filter((r) => r.value)
    .map((r) => ({
      ...r,
      source: IDENTITY_SOURCE_MAP[r.label] || 'SAP Readiness Check report',
    }));

  return {
    kpis,
    integration,
    identity,
    impacts,
    workshop,
    score: scoreObj,
    functionalAreas,
    customCode: code,
  };
}

const TONE_STYLES = {
  default: 'border-[var(--line)] bg-[var(--inset)]',
  info: 'border-sky-500/30 bg-sky-500/10',
  warn: 'border-amber-500/35 bg-amber-500/10',
  danger: 'border-rose-500/40 bg-rose-500/10',
  ok: 'border-emerald-500/35 bg-emerald-500/10',
};

function MetricTile({ label, value, sub, tone = 'default', source, sourceTitle, tooltipClassName }) {
  const tile = (
    <div
      className={`rounded-xl border px-3 py-3 ${TONE_STYLES[tone] || TONE_STYLES.default}`}
      data-testid={`metric-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
        {formatMetricValue(value)}
      </div>
      {sub ? (
        <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
          {sub}
        </div>
      ) : null}
    </div>
  );

  if (!source) return tile;
  return (
    <AuraSourceTooltip
      source={source}
      title={sourceTitle || 'Source'}
      contentClassName={tooltipClassName || ''}
      showHintIcon
    >
      {tile}
    </AuraSourceTooltip>
  );
}

function DiscoveryDashboard({ inventory, highlights }) {
  const {
    kpis,
    integration,
    identity,
    impacts,
    workshop,
    score,
    functionalAreas,
    customCode,
  } = buildDashboardMetrics(inventory);
  const hasInv = inventory && typeof inventory === 'object' && Object.keys(inventory).length > 0;

  if (!hasInv && !(highlights && highlights.length)) return null;

  const hasCustomCode = customCode && typeof customCode === 'object' && (
    numOrNull(customCode.total_findings) != null
    || numOrNull(customCode.objects_in_scope) != null
    || numOrNull(customCode.sycm_custom_objects) != null
    || numOrNull(customCode.functional_redesign) != null
    || numOrNull(customCode.technical_code_adoption) != null
    || (Array.isArray(customCode.top_impact_areas) && customCode.top_impact_areas.length > 0)
    || Boolean(customCode.adjustment_effort_notes)
  );

  const customTiles = hasCustomCode
    ? [
      {
        label: 'Total ATC findings',
        value: numOrNull(customCode.total_findings),
        tone: 'warn',
        source: CUSTOM_CODE_TILE_SOURCES['Total ATC findings'],
      },
      {
        label: 'Functional redesign',
        value:
          numOrNull(customCode.functional_redesign)
          ?? numOrNull(customCode.findings_in_scope_functional_manual),
        tone: 'danger',
        source: CUSTOM_CODE_TILE_SOURCES['Functional redesign'],
      },
      {
        label: 'Technical adoption',
        value: numOrNull(customCode.technical_code_adoption),
        tone: 'default',
        source: CUSTOM_CODE_TILE_SOURCES['Technical adoption'],
      },
      {
        label: 'Quick-fix capable',
        value:
          numOrNull(customCode.findings_in_scope_technical_quick_fix)
          ?? numOrNull(customCode.quick_fix_supported),
        tone: 'ok',
        source: CUSTOM_CODE_TILE_SOURCES['Quick-fix capable'],
      },
      {
        label: 'Tech. manual',
        value: numOrNull(customCode.findings_in_scope_technical_manual),
        tone: 'default',
        source: CUSTOM_CODE_TILE_SOURCES['Tech. manual'],
      },
      {
        label: 'SYCM custom objects',
        value: numOrNull(customCode.sycm_custom_objects),
        tone: 'info',
        source: CUSTOM_CODE_TILE_SOURCES['SYCM custom objects'],
      },
      {
        label: 'SYCM modifications',
        value: numOrNull(customCode.sycm_modifications),
        tone: 'info',
        source: CUSTOM_CODE_TILE_SOURCES['SYCM modifications'],
      },
      {
        label: 'Relevant SAP Notes',
        value: numOrNull(customCode.relevant_sap_notes),
        tone: 'info',
        source: CUSTOM_CODE_TILE_SOURCES['Relevant SAP Notes'],
      },
      {
        label: 'Objects with findings',
        value: numOrNull(customCode.objects_in_scope),
        tone: 'default',
        source: CUSTOM_CODE_TILE_SOURCES['Objects with findings'],
      },
    ].filter((t) => t.value != null)
    : [];

  const severityClass = (sev) => {
    const s = String(sev || '').toLowerCase();
    // Tinted borders/backgrounds only — body text uses theme foreground (readable in light + dark).
    if (s === 'blocker') return 'border-rose-500/40 bg-rose-500/10 text-foreground';
    if (s === 'high') return 'border-amber-500/40 bg-amber-500/10 text-foreground';
    if (s === 'medium') return 'border-sky-500/30 bg-sky-500/10 text-foreground';
    return 'border-[var(--line)] bg-[var(--inset)] text-foreground';
  };

  const signalLabel = (sig) => {
    const map = {
      finance_chapter: 'Finance chapter',
      simplification_relevant: 'Simplification item',
      business_function: 'Business function',
      process_discovery: 'Process discovery',
      fiori: 'Fiori',
      add_on: 'Add-on',
      other_explicit: 'Report text',
    };
    return map[sig] || sig || 'Report';
  };

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={100}>
    <div className="space-y-4" data-testid="discovery-dashboard">
      {identity.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">System identity</CardTitle>
            <CardDescription>Analyzed landscape from the readiness report cover scope</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {identity.map((row) => (
                <AuraSourceTooltip key={row.label} source={row.source}>
                  <div
                    className="rounded-lg border border-[var(--line)] bg-[var(--inset)] px-3 py-2"
                  >
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      {row.label}
                    </div>
                    <div className="mt-0.5 text-sm font-medium text-foreground">{row.value}</div>
                  </div>
                </AuraSourceTooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {kpis.length > 0 && (
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">Conversion readiness at a glance</h3>
            <span className="text-[11px] text-muted-foreground">
              Hover any tile for source · counts from readiness check inventory
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {kpis.map((k) => (
              <MetricTile
                key={k.id}
                label={k.label}
                value={k.value}
                sub={k.sub}
                tone={k.tone}
                source={k.source}
                sourceTitle={k.sourceTitle}
                tooltipClassName={k.tooltipClassName}
              />
            ))}
          </div>
          {score?.rationale ? (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Customization score: {score.rationale}
            </p>
          ) : null}
        </div>
      )}

      {integration.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Integration volumes</CardTitle>
            <CardDescription>
              Interface families with totals; amber risk chip = unavailable or blocked. Hover for RC workbook source.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {integration.map((row) => (
                <AuraSourceTooltip key={row.label} source={row.source}>
                  <div
                    className="rounded-xl border border-[var(--line)] bg-[var(--inset)] px-3 py-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-xs font-medium text-muted-foreground">{row.label}</span>
                      {row.risk > 0 ? (
                        <span className="rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                          {row.risk} risk
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                      {formatMetricValue(row.total)}
                    </div>
                    {row.hint && (
                      <div className="mt-1 text-[10px] leading-snug text-muted-foreground line-clamp-2">
                        {row.hint}
                      </div>
                    )}
                  </div>
                </AuraSourceTooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <AuraExternalSystemsLegend
        inventory={inventory}
        title="Connected systems in this project"
        description="Identified and described by the Analysis Agent from your readiness package — covers whatever systems appear in that report, not a fixed list."
      />

      {hasCustomCode && customTiles.length > 0 && (
        <Card data-testid="custom-code-dashboard">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Custom code (ATC / RC chapter 5)</CardTitle>
            <CardDescription>
              Figures only as stated in the readiness report.
              {customCode.analyzed_system
                ? ` Analysis system: ${customCode.analyzed_system}.`
                : ''}
              {' '}
              SYCM object totals are not the same as the conversion remediation backlog.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {customTiles.map((t) => (
                <MetricTile
                  key={t.label}
                  label={t.label}
                  value={t.value}
                  tone={t.tone}
                  source={t.source}
                />
              ))}
            </div>
            {(customCode.by_priority?.error != null
              || customCode.by_priority?.warning != null
              || customCode.by_priority?.information != null) && (
              <div className="flex flex-wrap gap-2 text-xs">
                {customCode.by_priority.error != null && (
                  <span className="rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-foreground">
                    Errors {formatMetricValue(customCode.by_priority.error)}
                  </span>
                )}
                {customCode.by_priority.warning != null && (
                  <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-foreground">
                    Warnings {formatMetricValue(customCode.by_priority.warning)}
                  </span>
                )}
                {customCode.by_priority.information != null && (
                  <span className="rounded-md border border-[var(--line)] bg-[var(--inset)] px-2 py-1 text-muted-foreground">
                    Info {formatMetricValue(customCode.by_priority.information)}
                  </span>
                )}
              </div>
            )}
            {customCode.adjustment_effort_notes && (
              <p className="text-xs text-muted-foreground">{customCode.adjustment_effort_notes}</p>
            )}
            {Array.isArray(customCode.top_impact_areas) && customCode.top_impact_areas.length > 0 && (
              <div>
                <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Impact areas named in report
                </h4>
                <ul className="list-disc space-y-0.5 pl-5 text-sm text-foreground">
                  {customCode.top_impact_areas.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(customCode.recommended_checks) && customCode.recommended_checks.length > 0 && (
              <div>
                <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Recommended additional checks
                </h4>
                <ul className="list-disc space-y-0.5 pl-5 text-sm text-foreground">
                  {customCode.recommended_checks.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {functionalAreas?.length > 0 && (
        <Card data-testid="functional-areas">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Functional footprint (evidenced only)</CardTitle>
            <CardDescription>
              Areas named in the readiness report with source evidence. Not a complete active/inactive
              module matrix — absent AR/AP/etc. means the report did not name them here, not that they
              are inactive.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {functionalAreas.map((area, i) => (
                <AuraSourceTooltip key={area.id || i} source={functionalAreaSource(area)}>
                  <div
                    className="rounded-xl border border-[var(--line)] bg-[var(--inset)] px-3 py-3 text-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">
                        {area.name}
                        {area.code ? (
                          <span className="ml-1 font-normal text-muted-foreground">
                            ({area.code})
                          </span>
                        ) : null}
                      </span>
                      <span className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-zinc-300">
                        {signalLabel(area.signal)}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground/80">Evidence: </span>
                      {area.evidence}
                    </p>
                    {area.notes ? (
                      <p className="mt-1 text-[11px] text-muted-foreground">{area.notes}</p>
                    ) : null}
                  </div>
                </AuraSourceTooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {impacts.length > 0 && (
        <Card data-testid="mandatory-impacts">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mandatory S/4HANA impacts</CardTitle>
            <CardDescription>
              Technical prerequisites and conversion gates from the readiness report
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {impacts.map((item, i) => (
                <AuraSourceTooltip key={item.id || i} source={impactSource(item)}>
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${severityClass(item.severity)}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{item.title || item.id || `Impact ${i + 1}`}</span>
                      {item.severity && (
                        <span className="rounded bg-foreground/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {item.severity}
                        </span>
                      )}
                      {item.category && (
                        <span className="text-[10px] text-muted-foreground">{item.category}</span>
                      )}
                    </div>
                    {item.evidence && (
                      <p className="mt-1 text-xs text-muted-foreground">{item.evidence}</p>
                    )}
                    {item.required_action && (
                      <p className="mt-1 text-xs font-medium">Action: {item.required_action}</p>
                    )}
                  </div>
                </AuraSourceTooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {workshop && (workshop.gap_brief
        || (workshop.interview_questions && workshop.interview_questions.length)
        || (workshop.hypotheses && workshop.hypotheses.length)
        || (workshop.effort_sizing && (
          workshop.effort_sizing.top_estimate_blockers?.length
          || workshop.effort_sizing.workstreams_in_scope?.length
        ))) && (
        <Card data-testid="workshop-pack">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Effort-sizing workshop pack</CardTitle>
            <CardDescription>
              Interview questions to quantify ECC → S/4 conversion effort (grounded in Readiness Check data)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {workshop.effort_sizing && (
              workshop.effort_sizing.target_s4_version
              || workshop.effort_sizing.complexity_band !== 'unknown'
              || (workshop.effort_sizing.workstreams_in_scope?.length > 0)
              || (workshop.effort_sizing.top_estimate_blockers?.length > 0)
            ) && (
              <div className="rounded-lg border border-[var(--line)] bg-[var(--inset)] p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Estimate snapshot
                </h4>
                <div className="flex flex-wrap gap-2">
                  {workshop.effort_sizing.target_s4_version && (
                    <Badge variant="outline">Target: {workshop.effort_sizing.target_s4_version}</Badge>
                  )}
                  {workshop.effort_sizing.conversion_approach
                    && workshop.effort_sizing.conversion_approach !== 'undetermined' && (
                    <Badge variant="outline">
                      Approach: {workshop.effort_sizing.conversion_approach}
                    </Badge>
                  )}
                  {workshop.effort_sizing.complexity_band
                    && workshop.effort_sizing.complexity_band !== 'unknown' && (
                    <Badge variant="outline" className={impactBadgeClass(workshop.effort_sizing.complexity_band)}>
                      Complexity: {workshop.effort_sizing.complexity_band}
                    </Badge>
                  )}
                </div>
                {Array.isArray(workshop.effort_sizing.workstreams_in_scope)
                  && workshop.effort_sizing.workstreams_in_scope.length > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Workstreams to size:{' '}
                    {workshop.effort_sizing.workstreams_in_scope.map(formatWorkstreamLabel).join(', ')}
                  </p>
                )}
                {Array.isArray(workshop.effort_sizing.top_estimate_blockers)
                  && workshop.effort_sizing.top_estimate_blockers.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                    {workshop.effort_sizing.top_estimate_blockers.map((blocker, i) => (
                      <li key={i}>{blocker}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {workshop.gap_brief && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Gap / conversion brief
                </h4>
                <p className="text-muted-foreground">{workshop.gap_brief}</p>
                {workshop.strategy_signals?.notes && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {workshop.strategy_signals.brownfield_convertible != null
                      && `Brownfield convertible: ${String(workshop.strategy_signals.brownfield_convertible)}. `}
                    {workshop.strategy_signals.notes}
                  </p>
                )}
              </div>
            )}
            {Array.isArray(workshop.interview_questions) && workshop.interview_questions.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Interview questions ({workshop.interview_questions.length})
                </h4>
                <ol className="list-decimal space-y-3 pl-5 text-foreground">
                  {workshop.interview_questions
                    .map((q, i) => normalizeInterviewQuestion(q, i))
                    .filter(Boolean)
                    .map((q) => (
                      <li key={q.id} className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">
                            {formatWorkstreamLabel(q.workstream)}
                          </Badge>
                          {q.estimation_impact && q.estimation_impact !== 'medium' && (
                            <Badge variant="outline" className={`text-[10px] ${impactBadgeClass(q.estimation_impact)}`}>
                              {q.estimation_impact} impact
                            </Badge>
                          )}
                          {q.expected_answer_type && q.expected_answer_type !== 'unknown' && (
                            <Badge variant="outline" className="text-[10px] text-muted-foreground">
                              {q.expected_answer_type.replace(/_/g, ' ')}
                            </Badge>
                          )}
                        </div>
                        <p>{q.question}</p>
                        {q.rc_evidence && (
                          <p className="text-xs text-muted-foreground">
                            RC evidence: {q.rc_evidence}
                          </p>
                        )}
                        {q.sizing_input && (
                          <p className="text-xs text-muted-foreground">
                            Sizing input: {q.sizing_input}
                          </p>
                        )}
                      </li>
                    ))}
                </ol>
              </div>
            )}
            {Array.isArray(workshop.hypotheses) && workshop.hypotheses.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Hypotheses to validate (effort bands)
                </h4>
                <ul className="list-disc space-y-1 pl-5 text-foreground">
                  {workshop.hypotheses.map((h, i) => (
                    <li key={i}>{h}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {highlights?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Key findings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2">
              {highlights.map((h, i) => (
                <AuraSourceTooltip
                  key={i}
                  source="Analysis Agent — key finding bullets from readiness package"
                >
                  <div
                    className="flex gap-2 rounded-lg border border-[var(--line)] bg-[var(--inset)] px-3 py-2 text-sm text-foreground"
                  >
                    <span className="mt-0.5 shrink-0 text-[10px] font-semibold tabular-nums text-muted-foreground">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span>{h}</span>
                  </div>
                </AuraSourceTooltip>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
    </TooltipProvider>
  );
}

function AgentStatusCard({ def, state }) {
  const isActive = state === 'active';
  const isDone = state === 'done';
  const isError = state === 'error';
  const isPending = state === 'pending' || state === 'idle';

  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl border p-5 transition-all duration-300
        ${isActive ? 'border-violet-500/50 bg-violet-500/10 shadow-md' : ''}
        ${isDone ? 'border-emerald-500/40 bg-emerald-500/5' : ''}
        ${isError ? 'border-red-500/40 bg-red-500/10' : ''}
        ${isPending && !isActive ? 'border-[var(--line)] bg-[var(--surface)] opacity-80' : ''}
      `}
      data-testid={`agent-card-${def.id}`}
      data-state={state}
    >
      {isActive && (
        <div
          className="pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-violet-500/10 to-transparent"
          aria-hidden
        />
      )}
      <div className="relative flex items-start gap-3">
        <div
          className={`
            mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full
            ${isActive ? 'bg-cp-grad text-white' : ''}
            ${isDone ? 'bg-emerald-500/20 text-emerald-300' : ''}
            ${isError ? 'bg-red-500/20 text-red-300' : ''}
            ${isPending && !isActive ? 'bg-[var(--inset)] text-muted-foreground' : ''}
          `}
        >
          {isActive && <Loader2 className="h-5 w-5 animate-spin" />}
          {isDone && <CheckCircle2 className="h-5 w-5" />}
          {isError && <Circle className="h-5 w-5" />}
          {isPending && !isActive && <Circle className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-foreground">{def.name}</h3>
            <Badge
              variant="outline"
              className={`rounded-full text-[10px] uppercase tracking-wide ${
                isActive ? 'border-violet-400/50 text-violet-200' : ''
              }`}
            >
              {isActive ? 'Working' : isDone ? 'Complete' : isError ? 'Failed' : 'Waiting'}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{def.description}</p>
        </div>
      </div>
    </div>
  );
}

function WizardSteps({ step }) {
  const order = [
    { id: 'mode', label: 'Mode' },
    { id: 'upload', label: 'Upload' },
    { id: 'running', label: 'Agents' },
    { id: 'results', label: 'Results' },
  ];
  const idx = {
    mode: 0,
    'offline-upload': 1,
    running: 2,
    results: 3,
  }[step] ?? 0;

  return (
    <ol className="flex flex-wrap gap-2" data-testid="wizard-steps">
      {order.map((s, i) => {
        const done = i < idx;
        const current = i === idx;
        return (
          <li
            key={s.id}
            className={`
              flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium
              ${current ? 'border-violet-500/50 bg-violet-500/15 text-foreground' : ''}
              ${done ? 'border-emerald-500/30 text-foreground' : ''}
              ${!done && !current ? 'border-[var(--line)] text-muted-foreground' : ''}
            `}
          >
            <span
              className={`
                flex h-5 w-5 items-center justify-center rounded-full text-[10px]
                ${current || done ? 'bg-cp-grad text-white' : 'bg-[var(--inset)]'}
              `}
            >
              {done ? '✓' : i + 1}
            </span>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}

function artifactByKind(artifacts, kind) {
  const list = Array.isArray(artifacts) ? artifacts : [];
  const matches = list.filter((a) => a.kind === kind);
  if (!matches.length) return null;
  if (kind === 'landscape_diagram' || kind === 'dataflow_diagram') {
    return matches.reduce((best, art) => {
      const count = diagramNodeCount(unwrapArtifactContent(art.content));
      const bestCount = best ? diagramNodeCount(unwrapArtifactContent(best.content)) : -1;
      return count > bestCount ? art : best;
    }, null);
  }
  return matches[0];
}

function buildAnalysisFromArtifacts(artifacts) {
  const summaryArt = artifactByKind(artifacts, 'summary');
  const landscape = artifactByKind(artifacts, 'landscape_diagram');
  const dataflow = artifactByKind(artifacts, 'dataflow_diagram');
  const inventoryArt = artifactByKind(artifacts, 'inventory');
  if (!summaryArt && !landscape && !dataflow && !inventoryArt) return null;
  const inventory = unwrapArtifactContent(inventoryArt?.content) || {};
  let landscapeDiagram = unwrapArtifactContent(landscape?.content) || {};
  let dataflowDiagram = unwrapArtifactContent(dataflow?.content) || {};
  landscapeDiagram = ensureDiagramFromInventory(landscapeDiagram, inventory, 'landscape');
  dataflowDiagram = ensureDiagramFromInventory(dataflowDiagram, inventory, 'dataflow');
  const summaryContent = unwrapArtifactContent(summaryArt?.content) || {};
  return {
    status: 'success',
    summary: summaryContent.markdown || summaryContent.summary || '',
    highlights: summaryContent.highlights || [],
    landscape_diagram: landscapeDiagram,
    dataflow_diagram: dataflowDiagram,
    inventory,
  };
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Client-side PDF via browser print → Save as PDF (no extra deps).
 * Mirrors DiscoveryDashboard sections so "Download full report" matches the UI.
 */
function downloadResultsPdf({ project, company, analysis, landscapeSvg, dataflowSvg }) {
  const highlights = Array.isArray(analysis?.highlights) ? analysis.highlights : [];
  const inv = analysis?.inventory || {};
  const title = project?.name || 'Transformation discovery';
  const companyLabel = company?.name || company?.code || '';
  const {
    kpis,
    integration,
    identity,
    impacts,
    workshop,
    score,
    functionalAreas,
    customCode,
  } = buildDashboardMetrics(inv);
  const externalSystems = getExternalSystemsFromInventory(inv);

  const formatSourceHtml = (source) => {
    const raw = String(source || '').trim();
    if (!raw) return '';
    return raw
      .split(/\n+/)
      .map((line) => escapeHtml(line.trim()))
      .filter(Boolean)
      .join('<br/>');
  };

  const sourceBlock = (source, { compact = false } = {}) => {
    const html = formatSourceHtml(source);
    if (!html) return '';
    const cls = compact ? 'source source-compact' : 'source';
    return `<div class="${cls}"><span class="source-label">Source</span> ${html}</div>`;
  };

  const listSection = (heading, items) => {
    if (!Array.isArray(items) || !items.length) return '';
    return `<h2>${escapeHtml(heading)}</h2><ul>${items
      .map((x) => `<li>${escapeHtml(String(x))}</li>`)
      .join('')}</ul>`;
  };

  const kvGrid = (rows) => {
    if (!rows?.length) return '';
    return `<div class="grid">${rows
      .map(
        (r) =>
          `<div class="tile"><div class="label">${escapeHtml(r.label)}</div>`
          + `<div class="value">${escapeHtml(String(r.value ?? '—'))}</div>`
          + (r.sub
            ? `<div class="sub">${escapeHtml(String(r.sub))}</div>`
            : '')
          + sourceBlock(r.source, { compact: true })
          + `</div>`,
      )
      .join('')}</div>`;
  };

  const identityRows = (identity || []).map((r) => ({
    label: r.label,
    value: r.value,
    source: r.source,
  }));

  const kpiRows = (kpis || [])
    .filter((k) => k.value != null && k.value !== '')
    .map((k) => ({
      label: k.label,
      value: formatMetricValue(k.value),
      sub: k.sub,
      source: k.source,
    }));

  const integRows = (integration || []).map((row) => ({
    label: row.label,
    value: formatMetricValue(row.total),
    sub: [
      row.risk > 0 ? `${row.risk} risk` : null,
      row.hint || null,
    ]
      .filter(Boolean)
      .join(' · '),
    source: row.source,
  }));

  const hasCustomCode = customCode && typeof customCode === 'object' && (
    numOrNull(customCode.total_findings) != null
    || numOrNull(customCode.objects_in_scope) != null
    || numOrNull(customCode.sycm_custom_objects) != null
    || numOrNull(customCode.functional_redesign) != null
    || numOrNull(customCode.technical_code_adoption) != null
    || (Array.isArray(customCode.top_impact_areas) && customCode.top_impact_areas.length > 0)
    || Boolean(customCode.adjustment_effort_notes)
  );

  const customTiles = hasCustomCode
    ? [
      { label: 'Total ATC findings', value: numOrNull(customCode.total_findings) },
      {
        label: 'Functional redesign',
        value:
          numOrNull(customCode.functional_redesign)
          ?? numOrNull(customCode.findings_in_scope_functional_manual),
      },
      { label: 'Technical adoption', value: numOrNull(customCode.technical_code_adoption) },
      {
        label: 'Quick-fix capable',
        value:
          numOrNull(customCode.findings_in_scope_technical_quick_fix)
          ?? numOrNull(customCode.quick_fix_supported),
      },
      {
        label: 'Tech. manual',
        value: numOrNull(customCode.findings_in_scope_technical_manual),
      },
      { label: 'SYCM custom objects', value: numOrNull(customCode.sycm_custom_objects) },
      { label: 'SYCM modifications', value: numOrNull(customCode.sycm_modifications) },
      { label: 'Relevant SAP Notes', value: numOrNull(customCode.relevant_sap_notes) },
      { label: 'Objects with findings', value: numOrNull(customCode.objects_in_scope) },
    ]
      .filter((t) => t.value != null)
      .map((t) => ({
        label: t.label,
        value: formatMetricValue(t.value),
        source: CUSTOM_CODE_TILE_SOURCES[t.label] || 'SAP Readiness Check report — Custom Code chapter',
      }))
    : [];

  const signalLabel = (sig) => {
    const map = {
      finance_chapter: 'Finance chapter',
      simplification_relevant: 'Simplification item',
      business_function: 'Business function',
      process_discovery: 'Process discovery',
      fiori: 'Fiori',
      add_on: 'Add-on',
      other_explicit: 'Report text',
    };
    return map[sig] || sig || 'Report';
  };

  const functionalHtml = (functionalAreas || []).length
    ? `<h2>Functional footprint (evidenced only)</h2><ul>${functionalAreas
      .map((area) => {
        const name = area.name + (area.code ? ` (${area.code})` : '');
        const bits = [
          `<strong>${escapeHtml(name)}</strong>`,
          `<em>${escapeHtml(signalLabel(area.signal))}</em>`,
          area.evidence ? `Evidence: ${escapeHtml(area.evidence)}` : null,
          area.notes ? escapeHtml(area.notes) : null,
        ].filter(Boolean);
        return `<li>${bits.join(' — ')}${sourceBlock(functionalAreaSource(area))}</li>`;
      })
      .join('')}</ul>`
    : '';

  const impactsHtml = (impacts || []).length
    ? `<h2>Mandatory S/4HANA impacts</h2><ul>${impacts
      .map((item, i) => {
        const titleText = item.title || item.id || `Impact ${i + 1}`;
        const bits = [
          `<strong>${escapeHtml(titleText)}</strong>`,
          item.severity ? escapeHtml(String(item.severity).toUpperCase()) : null,
          item.category ? escapeHtml(item.category) : null,
          item.evidence ? `Evidence: ${escapeHtml(item.evidence)}` : null,
          item.required_action
            ? `Action: ${escapeHtml(item.required_action)}`
            : null,
        ].filter(Boolean);
        return `<li>${bits.join(' — ')}${sourceBlock(impactSource(item))}</li>`;
      })
      .join('')}</ul>`
    : '';

  let workshopHtml = '';
  if (
    workshop
    && (workshop.gap_brief
      || (workshop.interview_questions && workshop.interview_questions.length)
      || (workshop.hypotheses && workshop.hypotheses.length)
      || (workshop.effort_sizing
        && (workshop.effort_sizing.top_estimate_blockers?.length
          || workshop.effort_sizing.workstreams_in_scope?.length
          || workshop.effort_sizing.target_s4_version
          || (workshop.effort_sizing.complexity_band
            && workshop.effort_sizing.complexity_band !== 'unknown'))))
  ) {
    const parts = [
      '<h2>Effort-sizing workshop pack</h2>',
      sourceBlock(
        resolveSectionSource(
          workshop,
          'Analysis Agent (derived from readiness package) — effort-sizing workshop',
        ),
      ),
    ];
    const es = workshop.effort_sizing || {};
    const snap = [];
    if (es.target_s4_version) snap.push(`Target: ${es.target_s4_version}`);
    if (es.conversion_approach && es.conversion_approach !== 'undetermined') {
      snap.push(`Approach: ${es.conversion_approach}`);
    }
    if (es.complexity_band && es.complexity_band !== 'unknown') {
      snap.push(`Complexity: ${es.complexity_band}`);
    }
    if (Array.isArray(es.workstreams_in_scope) && es.workstreams_in_scope.length) {
      snap.push(
        `Workstreams: ${es.workstreams_in_scope.map(formatWorkstreamLabel).join(', ')}`,
      );
    }
    if (snap.length) {
      parts.push(`<p>${escapeHtml(snap.join(' · '))}</p>`);
    }
    if (Array.isArray(es.top_estimate_blockers) && es.top_estimate_blockers.length) {
      parts.push(
        `<h3>Estimate blockers</h3><ul>${es.top_estimate_blockers
          .map((b) => `<li>${escapeHtml(String(b))}</li>`)
          .join('')}</ul>`,
      );
    }
    if (workshop.gap_brief) {
      parts.push(
        `<h3>Gap / conversion brief</h3><p>${escapeHtml(workshop.gap_brief)}</p>`,
      );
    }
    if (workshop.strategy_signals?.notes) {
      const brown =
        workshop.strategy_signals.brownfield_convertible != null
          ? `Brownfield convertible: ${String(workshop.strategy_signals.brownfield_convertible)}. `
          : '';
      parts.push(
        `<p class="sub">${escapeHtml(brown + workshop.strategy_signals.notes)}</p>`,
      );
    }
    const questions = Array.isArray(workshop.interview_questions)
      ? workshop.interview_questions
        .map((q, i) => normalizeInterviewQuestion(q, i))
        .filter(Boolean)
      : [];
    if (questions.length) {
      parts.push(
        `<h3>Interview questions (${questions.length})</h3><ol>${questions
          .map((q) => {
            const meta = [
              formatWorkstreamLabel(q.workstream),
              q.estimation_impact && q.estimation_impact !== 'medium'
                ? `${q.estimation_impact} impact`
                : null,
              q.expected_answer_type && q.expected_answer_type !== 'unknown'
                ? q.expected_answer_type.replace(/_/g, ' ')
                : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              `<li><div class="sub">${escapeHtml(meta)}</div>`
              + `<div>${escapeHtml(q.question)}</div>`
              + (q.rc_evidence
                ? `<div class="sub">RC evidence: ${escapeHtml(q.rc_evidence)}</div>`
                : '')
              + (q.sizing_input
                ? `<div class="sub">Sizing input: ${escapeHtml(q.sizing_input)}</div>`
                : '')
              + `</li>`
            );
          })
          .join('')}</ol>`,
      );
    }
    if (Array.isArray(workshop.hypotheses) && workshop.hypotheses.length) {
      parts.push(
        `<h3>Hypotheses to validate</h3><ul>${workshop.hypotheses
          .map((h) => `<li>${escapeHtml(String(h))}</li>`)
          .join('')}</ul>`,
      );
    }
    workshopHtml = parts.join('');
  }

  const externalHtml = externalSystems.length
    ? `<h2>Connected systems</h2><ul>${externalSystems
      .map((sys) => {
        const bits = [
          `<strong>${escapeHtml(sys.name)}</strong>`,
          sys.category ? escapeHtml(sys.category) : null,
          sys.description ? escapeHtml(sys.description) : null,
        ].filter(Boolean);
        const src = sys.identificationSource
          || 'Analysis Agent — identified from readiness package';
        return `<li>${bits.join(' — ')}${sourceBlock(src)}</li>`;
      })
      .join('')}</ul>`
    : '';

  const customCodeHtml = customTiles.length
    ? `<h2>Custom code (ATC / RC chapter 5)</h2>`
      + (customCode.analyzed_system
        ? `<p class="sub">Analysis system: ${escapeHtml(customCode.analyzed_system)}</p>`
        : '')
      + kvGrid(customTiles)
      + (customCode.by_priority
        ? `<p class="sub">${[
          customCode.by_priority.error != null
            ? `Errors ${formatMetricValue(customCode.by_priority.error)}`
            : null,
          customCode.by_priority.warning != null
            ? `Warnings ${formatMetricValue(customCode.by_priority.warning)}`
            : null,
          customCode.by_priority.information != null
            ? `Info ${formatMetricValue(customCode.by_priority.information)}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}</p>`
        : '')
      + (customCode.adjustment_effort_notes
        ? `<p>${escapeHtml(customCode.adjustment_effort_notes)}</p>`
        : '')
      + listSection('Impact areas named in report', customCode.top_impact_areas)
      + listSection('Recommended additional checks', customCode.recommended_checks)
    : '';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} — Discovery Report</title>
  <style>
    body { font-family: system-ui, sans-serif; color: #111; margin: 32px; line-height: 1.45; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    h2 { font-size: 16px; margin: 28px 0 10px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h3 { font-size: 13px; margin: 16px 0 8px; }
    .meta { color: #555; font-size: 12px; margin-bottom: 24px; }
    .sub { color: #555; font-size: 11px; margin-top: 2px; }
    .source { color: #444; font-size: 10px; margin-top: 6px; line-height: 1.35; }
    .source-compact { margin-top: 8px; border-top: 1px dashed #ddd; padding-top: 6px; }
    .source-label { font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: #666; }
    ul, ol { margin: 0 0 12px 18px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; margin: 0 0 12px; }
    .tile { border: 1px solid #ddd; border-radius: 8px; padding: 10px; page-break-inside: avoid; }
    .tile .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #666; }
    .tile .value { font-size: 18px; font-weight: 600; margin-top: 2px; }
    .diagrams { display: grid; grid-template-columns: 1fr; gap: 20px; }
    .diagram-box { border: 1px solid #ddd; border-radius: 8px; padding: 12px; page-break-inside: avoid; }
    .diagram-box h3 { font-size: 13px; margin: 0 0 8px; }
    svg { max-width: 100%; height: auto; }
    @media print {
      body { margin: 16px; }
      .diagrams { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">
    Halo Transformations · Offline discovery
    ${companyLabel ? ` · Company: ${escapeHtml(companyLabel)}` : ''}
    · Generated ${escapeHtml(new Date().toLocaleString())}
  </div>
  ${identityRows.length ? `<h2>System identity</h2>${kvGrid(identityRows)}` : ''}
  ${
    kpiRows.length
      ? `<h2>Conversion readiness at a glance</h2>${kvGrid(kpiRows)}`
        + (score?.rationale
          ? `<p class="sub">Customization score: ${escapeHtml(score.rationale)}</p>`
          : '')
      : ''
  }
  ${integRows.length ? `<h2>Integration volumes</h2>${kvGrid(integRows)}` : ''}
  ${externalHtml}
  ${customCodeHtml}
  ${functionalHtml}
  ${impactsHtml}
  ${workshopHtml}
  ${listSection('Blockers', inv.blockers)}
  ${listSection('Next transformation steps', inv.next_steps)}
  ${listSection('Open questions', inv.open_questions)}
  ${
    highlights.length
      ? `<h2>Key findings</h2>`
        + sourceBlock('Analysis Agent — key finding bullets from readiness package')
        + `<ul>${highlights
          .map((h) => `<li>${escapeHtml(String(h))}</li>`)
          .join('')}</ul>`
      : ''
  }
  <h2>Diagrams</h2>
  <div class="diagrams">
    <div class="diagram-box">
      <h3>Landscape</h3>
      ${landscapeSvg || '<p>No landscape diagram.</p>'}
    </div>
    <div class="diagram-box">
      <h3>Dataflow</h3>
      ${dataflowSvg || '<p>No dataflow diagram.</p>'}
    </div>
  </div>
  <script>
    window.onload = function () {
      setTimeout(function () { window.print(); }, 250);
    };
  </script>
</body>
</html>`;

  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(project?.name || 'discovery-report').replace(/\s+/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

export default function AuraProjectDetail() {
  const { projectId } = useParams();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('transformations', 'update');
  const landscapeDiagramRef = useRef(null);
  const dataflowDiagramRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [runs, setRuns] = useState([]);
  const [step, setStep] = useState('mode'); // mode | offline-upload | running | results
  const [mode, setMode] = useState(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [lastRunId, setLastRunId] = useState(null);
  const [agentStates, setAgentStates] = useState({
    landscape: 'idle',
    analysis: 'idle',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAuraProject(projectId);
      setProject(data.project || null);
      setCompany(data.company || null);
      setRuns(Array.isArray(data.runs) ? data.runs : []);

      const built = buildAnalysisFromArtifacts(data.artifacts || []);
      if (built) {
        setStep('results');
        setMode('offline');
        setAnalysis(built);
        setAgentStates({ landscape: 'done', analysis: 'done' });
        const successRun = (data.runs || []).find((r) => r.status === 'succeeded');
        if (successRun) setLastRunId(successRun.id);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load project';
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const display = useMemo(() => {
    if (
      analysis?.status === 'success'
      || analysis?.landscape_diagram?.nodes?.length
      || analysis?.inventory
    ) {
      return analysis;
    }
    return null;
  }, [analysis]);

  const diagramHeader = useMemo(() => {
    const inv = display?.inventory || {};
    return {
      customer: inv.customer_name || company?.name || '',
      customerNumber: inv.customer_number || '',
      sid: inv.sid || '',
      target: inv.target_s4_version || '',
      date: inv.analysis_date || '',
    };
  }, [display, company]);

  const chooseMode = (m) => {
    if (!canUpdate) return;
    setMode(m);
    setError('');
    if (m === 'online') {
      setError(
        'Online transformation is not available yet. Select Offline and upload a readiness PDF or RC export ZIP.',
      );
      return;
    }
    setStep('offline-upload');
  };

  const runOffline = async () => {
    if (!canUpdate) return;
    if (!file) {
      setError('Select a readiness PDF or RC export package (ZIP / Word / Excel) first.');
      return;
    }
    setAnalyzing(true);
    setError('');
    setStep('running');
    setAgentStates({ landscape: 'active', analysis: 'pending' });

    // After landscape package-up window, flip to analysis agent while backend works.
    const handoffTimer = setTimeout(() => {
      setAgentStates((s) =>
        s.analysis === 'done' || s.analysis === 'error'
          ? s
          : { landscape: 'done', analysis: 'active' },
      );
    }, 1800);

    try {
      const result = await analyzeOffline(projectId, file);
      setLastRunId(result.run_id || null);

      if ((result.status === 'accepted' || result.status === 'running') && result.run_id) {
        const finished = await waitForAuraOfflineRun(projectId, result.run_id);
        clearTimeout(handoffTimer);
        if (String(finished.run?.status || '').toLowerCase() === 'failed') {
          setAgentStates({ landscape: 'done', analysis: 'error' });
          setError(finished.run?.error_message || 'Analysis failed');
          setStep('offline-upload');
          return;
        }
        setAgentStates({ landscape: 'done', analysis: 'done' });
        setStep('results');
        await load();
        return;
      }

      clearTimeout(handoffTimer);
      const next = result.analysis || result;
      setAnalysis(next);

      if (result.status === 'success' || next?.status === 'success') {
        setAgentStates({ landscape: 'done', analysis: 'done' });
        setStep('results');
        await load();
      } else {
        setAgentStates({ landscape: 'done', analysis: 'error' });
        setError(result.error || next?.error || 'Analysis failed');
        if (result.run_id || next?.summary) setStep('results');
      }
    } catch (err) {
      clearTimeout(handoffTimer);
      setAgentStates({ landscape: 'done', analysis: 'error' });
      const detail = err?.response?.data?.detail || err?.message || 'Offline analysis failed';
      setError(String(detail));
      setStep('offline-upload');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!display) return;
    const landscapeSvg =
      landscapeDiagramRef.current?.exportHtml?.() || '';
    const dataflowSvg =
      dataflowDiagramRef.current?.exportHtml?.() || '';
    downloadResultsPdf({
      project,
      company,
      analysis: display,
      landscapeSvg,
      dataflowSvg,
    });
  };

  const handleDownloadInventory = () => {
    if (!display) return;
    const payload = {
      project_id: projectId,
      project_name: project?.name,
      company: company?.code || company?.name,
      exported_at: new Date().toISOString(),
      highlights: display.highlights || [],
      inventory: display.inventory || {},
      landscape_diagram: display.landscape_diagram || {},
      dataflow_diagram: display.dataflow_diagram || {},
      summary: display.summary || '',
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(project?.name || 'transformation-inventory')
      .replace(/\s+/g, '-')
      .toLowerCase()}-inventory.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetWizard = () => {
    if (!canUpdate) return;
    setStep('mode');
    setMode(null);
    setFile(null);
    setError('');
    setAgentStates({ landscape: 'idle', analysis: 'idle' });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="aura-project-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading project…
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-4" data-testid="aura-project-missing">
        <Link to="/transformations" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to dashboard
        </Link>
        <p className="text-sm text-red-300">{error || 'Project not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="aura-project-detail">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to={
              project.company_id
                ? `/transformations?company=${encodeURIComponent(project.company_id)}`
                : '/transformations'
            }
            className="mb-2 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Dashboard
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              {project.name}
            </h1>
            <Badge
              variant="outline"
              className={`rounded-full ${statusBadgeClass(project.status)}`}
            >
              {statusLabel(project.status)}
            </Badge>
            {company?.code && (
              <Badge variant="outline" className="rounded-full">
                {company.code}
              </Badge>
            )}
          </div>
          {project.description ? (
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{project.description}</p>
          ) : null}
          <p className="mt-3 text-xs text-muted-foreground">
            Project discovery wizard · Offline readiness analysis
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {step === 'results' && display && (
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadPdf}
              data-testid="download-results-pdf"
            >
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
          )}
          {step === 'results' && canUpdate && (
            <Button type="button" variant="secondary" onClick={resetWizard}>
              Run discovery again
            </Button>
          )}
        </div>
      </div>

      <WizardSteps step={step} />

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {/* Step 1: Online / Offline */}
      {step === 'mode' && (
        <Card data-testid="aura-wizard-mode">
          <CardHeader>
            <CardTitle>Is this an online or offline transformation?</CardTitle>
            <CardDescription>
              Choose how landscape discovery should run for this project. Offline uses a readiness
              report PDF; online (live landscape inventory) is not available yet.
              {!canUpdate
                ? ' View-only access: Transformations update permission is required to start discovery.'
                : ''}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <button
              type="button"
              onClick={() => chooseMode('online')}
              disabled={!canUpdate}
              className="rounded-xl border border-[var(--line)] bg-[var(--inset)] p-5 text-left transition hover:border-sky-500/40 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="aura-mode-online"
            >
              <Radio className="mb-3 h-6 w-6 text-sky-300" />
              <h3 className="font-semibold">Online</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Landscape Discovery Agent inventories systems live (coming soon).
              </p>
            </button>
            <button
              type="button"
              onClick={() => chooseMode('offline')}
              disabled={!canUpdate}
              className="rounded-xl border border-[var(--line)] bg-[var(--inset)] p-5 text-left transition hover:border-violet-500/40 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="aura-mode-offline"
            >
              <CloudOff className="mb-3 h-6 w-6 text-violet-300" />
              <h3 className="font-semibold">Offline</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Landscape Discovery packages a readiness PDF or RC export ZIP; Analysis
                Agent produces dashboard metrics and connected diagrams.
              </p>
            </button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload */}
      {step === 'offline-upload' && canUpdate && (
        <Card data-testid="aura-wizard-upload">
          <CardHeader>
            <CardTitle>Upload readiness report</CardTitle>
            <CardDescription>
              Provide the SAP Readiness Check PDF, or a ZIP of the RC export (Word + Excel
              interface workbooks). Extra Excel/Word files help name connected systems.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label
              className="
                flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl
                border border-dashed border-[var(--line)] bg-[var(--inset)] px-6 py-10
                text-center hover:border-violet-500/40
              "
            >
              <FileUp className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm font-medium">
                {file ? file.name : 'Choose readiness PDF or RC export ZIP'}
              </span>
              <span className="text-xs text-muted-foreground">
                PDF, ZIP, DOCX, or XLSX · max 40 MB
              </span>
              <input
                type="file"
                accept=".pdf,.zip,.docx,.xlsx,application/pdf,application/zip"
                className="hidden"
                data-testid="aura-pdf-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setStep('mode')} disabled={analyzing}>
                Back
              </Button>
              <Button
                type="button"
                onClick={runOffline}
                disabled={!file || analyzing}
                data-testid="aura-run-offline"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting agents…
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Start offline discovery
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Agents working */}
      {step === 'running' && (
        <div className="space-y-4" data-testid="aura-wizard-agents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="h-5 w-5" />
                Agents running
              </CardTitle>
              <CardDescription>
                Offline path only shows the sub-agents that handle your report—not orchestrators.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              {AGENT_DEFS.map((def) => (
                <AgentStatusCard key={def.id} def={def} state={agentStates[def.id]} />
              ))}
            </CardContent>
          </Card>
          <p className="text-center text-sm text-muted-foreground">
            {agentStates.analysis === 'active'
              ? 'Analysis Agent is reading the readiness package… This can take a few minutes.'
              : 'Landscape Discovery Agent is packaging the readiness upload…'}
          </p>
        </div>
      )}

      {/* Step 4: Results */}
      {step === 'results' && display && (
        <div className="space-y-6" data-testid="aura-results">
          <AuraProjectChat projectId={project?.id || projectId} />

          <div className="grid gap-4 md:grid-cols-2">
            {AGENT_DEFS.map((def) => (
              <AgentStatusCard
                key={def.id}
                def={def}
                state={agentStates[def.id] || 'done'}
              />
            ))}
          </div>

          {lastRunId && (
            <p className="text-xs text-muted-foreground">Run ID: {lastRunId}</p>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadInventory}
              data-testid="download-inventory-json"
            >
              <Download className="mr-2 h-4 w-4" />
              Export inventory JSON
            </Button>
            <Button type="button" onClick={handleDownloadPdf} data-testid="download-results-pdf-main">
              <Download className="mr-2 h-4 w-4" />
              Download full report (PDF)
            </Button>
          </div>

          <DiscoveryDashboard
            inventory={display.inventory}
            highlights={display.highlights}
          />

          <div className="grid gap-6 xl:grid-cols-1">
            <Card data-testid="result-landscape">
              <CardHeader>
                <CardTitle className="text-base">Landscape</CardTitle>
                <CardDescription>
                  Connected landscape from the readiness package. Inner SAP tiles are modules named in the report.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TransformationDiagram
                  ref={landscapeDiagramRef}
                  diagram={display.landscape_diagram}
                  header={diagramHeader}
                  areas={display.inventory?.functional_areas}
                  inventory={display.inventory}
                  tall
                />
              </CardContent>
            </Card>
            <Card data-testid="result-dataflow">
              <CardHeader>
                <CardTitle className="text-base">Integration & dataflow</CardTitle>
                <CardDescription>
                  Hub-and-spoke dataflow (core ERP center, sources and destinations around it).
                  Colored arrows follow the data-type legend; edges stay visible above blocks.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TransformationDiagram
                  ref={dataflowDiagramRef}
                  diagram={display.dataflow_diagram}
                  header={diagramHeader}
                  areas={display.inventory?.functional_areas}
                  inventory={display.inventory}
                  tall
                />
              </CardContent>
            </Card>
          </div>

          {runs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent runs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {runs.slice(0, 5).map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--line)] py-2 last:border-0"
                  >
                    <span className="font-mono text-xs text-muted-foreground">
                      {r.id.slice(0, 8)}…
                    </span>
                    <span className="capitalize">{r.mode}</span>
                    <Badge variant="outline">{r.status}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {step === 'results' && !display && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Analysis finished without a full payload. Try Run discovery again with the readiness PDF.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
